import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from './redux/hooks';
import { getCurrentUser } from './redux/slices/authSlice';
import MainLayout from './components/layout/MainLayout';
import AuthLayout from './components/layout/AuthLayout';
import LoadingScreen from './components/common/LoadingScreen';

// Pages
import Login from './pages/auth/Login';
import FirstLogin from './pages/auth/FirstLogin';
import Home from './pages/home/Home';
import CreditControl from './pages/creditControl/CreditControl';
import Debts from './pages/creditControl/Debts';
import Analytics from './pages/analytics/Analytics';
import Settings from './pages/settings/Settings';
import CourseManagement from './pages/courseManagement/CourseManagement';
import Admin from './pages/admin/Admin';
import Onboarding from './pages/onboarding/Onboarding';
import Management from './pages/management/Management';
import Tutors from './pages/tutors/Tutors';

// Protected route wrapper
const ProtectedRoute: React.FC<{ 
  element: React.ReactNode;
  requiredPermission?: string;
}> = ({ element, requiredPermission }) => {
  const { isAuthenticated, user, isLoading } = useAppSelector((state) => state.auth);
  const location = useLocation();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    // Redirect to login if not authenticated
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user needs to complete first login
  if (user && user.firstLogin && !location.pathname.startsWith('/first-login')) {
    console.log('User needs to complete first login, redirecting...');
    return <Navigate to={`/first-login/${user.userId}`} replace />;
  }

  // Check if user has the required permission
  if (requiredPermission && user && user.permissions) {
    if (!user.permissions.includes(requiredPermission as any)) {
      console.log(`User does not have required permission: ${requiredPermission}`);
      // Redirect to home if user doesn't have required permission
      return <Navigate to="/" replace />;
    }
  }

  return <>{element}</>;
};

const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route 
            path="/login" 
            element={<Login />} 
          />
          <Route 
            path="/first-login/:userId" 
            element={<FirstLogin />} 
          />
        </Route>

        {/* Main app routes */}
        <Route element={<MainLayout />}>
          <Route 
            path="/" 
            element={<ProtectedRoute element={<Home />} requiredPermission="home" />} 
          />
          
          <Route 
            path="/credit-control" 
            element={<ProtectedRoute element={<CreditControl />} requiredPermission="credit_control" />} 
          />
          <Route 
            path="/debts" 
            element={<ProtectedRoute element={<Debts />} requiredPermission="credit_control" />} 
          />
          <Route 
            path="/analytics" 
            element={<ProtectedRoute element={<Analytics />} requiredPermission="analytics" />} 
          />
          <Route 
            path="/settings" 
            element={<ProtectedRoute element={<Settings />} requiredPermission="settings" />} 
          />
          <Route 
            path="/course-management" 
            element={<ProtectedRoute element={<CourseManagement />} requiredPermission="course_management" />} 
          />
          <Route 
            path="/admin" 
            element={<ProtectedRoute element={<Admin />} requiredPermission="admin" />} 
          />
          <Route 
            path="/onboarding" 
            element={<ProtectedRoute element={<Onboarding />} requiredPermission="onboarding" />} 
          />
          <Route 
            path="/management" 
            element={<ProtectedRoute element={<Management />} requiredPermission="management" />} 
          />
          <Route 
            path="/tutors" 
            element={<ProtectedRoute element={<Tutors />} requiredPermission="tutor" />} 
          />
        </Route>

        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRouter;
